import type { HabitCategory, ObjectiveView } from '@contracts';
import { habitCategoryMeta } from '@ui/shared/habits/habitCategory';

type LinkedHabit = ObjectiveView['linkedHabits'][number];

/**
 * "Linked habits" trailing section (objective-detail.md §1.5, phase-3-plan.md
 * HL-3/AC-P3). A new section beneath the module list — own label, own row
 * anatomy (`LinkedHabitRow`, deliberately NOT `ModuleRow`), own whitespace
 * gap above it, so it never reads as "one more module" or blends with the
 * realism card (AC-P3's "visually separate... no visual blending").
 *
 * `ObjectiveView.linkedHabits` carries only habitId/name/currentWeek; category
 * and habitKind (needed for the icon tile and build-vs-avoid phrasing) are
 * joined in by the caller from `listHabits()` via `habitsById` — a display-
 * layer join over two already-fetched view models, not new domain math.
 */
export function LinkedHabitsSection({
  linkedHabits,
  habitsById,
}: {
  readonly linkedHabits: ObjectiveView['linkedHabits'];
  readonly habitsById: ReadonlyMap<string, { readonly category: HabitCategory; readonly habitKind: 'build' | 'avoid' }>;
}) {
  // Section omission rule (§1.5): render section (with empty caption)
  // whenever linkedHabits.length === 0 -- an objective that never had a
  // linked habit and one that just lost its last one to archiving read
  // identically, which is fine (both legitimately invite the same next
  // action). This is NOT the same "absent, not an empty card" rule used
  // elsewhere (e.g. Archived group) -- §1.5 explicitly calls for the
  // section (label + empty caption) to always render here.
  return (
    <div
      style={{
        margin: 'var(--space-6) var(--layout-page-margin-phone) 0',
        paddingTop: 'var(--space-4)',
        borderTop: '1px solid var(--border-subtle)',
      }}
    >
      <div
        style={{
          fontSize: 'var(--type-label-size)',
          lineHeight: 'var(--type-label-line)',
          letterSpacing: 'var(--type-label-tracking)',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          fontWeight: 600,
        }}
      >
        Linked habits
      </div>

      {linkedHabits.length === 0 ? (
        <p
          style={{
            margin: 'var(--space-3) 0 0',
            fontSize: 'var(--type-body-size)',
            color: 'var(--text-secondary)',
          }}
        >
          No habits linked yet. Link one from the habit&apos;s editor to add it here.
        </p>
      ) : (
        <div style={{ marginTop: 'var(--space-3)' }}>
          {linkedHabits.map((habit, i) => (
            <LinkedHabitRow
              key={habit.habitId}
              habit={habit}
              meta={habitsById.get(habit.habitId) ?? null}
              isLast={i === linkedHabits.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function LinkedHabitRow({
  habit,
  meta,
  isLast,
}: {
  readonly habit: LinkedHabit;
  readonly meta: { readonly category: HabitCategory; readonly habitKind: 'build' | 'avoid' } | null;
  readonly isLast: boolean;
}) {
  const habitKind = meta?.habitKind ?? 'build';
  const { planned, actual, met } = habit.currentWeek;

  let adherenceText: string;
  let ariaSuffix: string;
  let showMetTag = false;

  if (habitKind === 'avoid') {
    adherenceText = `${actual} of ${planned} used this week`;
    ariaSuffix = `${actual} of ${planned} used this week`;
  } else if (met) {
    adherenceText = `${actual} / ${planned} this week`;
    ariaSuffix = `${actual} of ${planned} this week, target met`;
    showMetTag = true;
  } else {
    adherenceText = `${actual} / ${planned} this week`;
    ariaSuffix = `${actual} of ${planned} this week`;
  }

  return (
    <div
      aria-label={`${habit.name}, ${ariaSuffix}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-3) 0',
        borderBottom: isLast ? 'none' : '1px solid var(--border-subtle)',
      }}
    >
      {meta && <CategoryIconTile category={meta.category} />}

      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 'var(--type-body-size)',
          fontWeight: 500,
          color: 'var(--text-primary)',
        }}
      >
        {habit.name}
      </span>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flex: 'none' }} aria-hidden="true">
        <span
          className="num"
          style={{
            fontSize: 'var(--type-caption-size)',
            color: habitKind === 'build' && met ? 'var(--text-primary)' : 'var(--text-secondary)',
          }}
        >
          {adherenceText}
        </span>
        {showMetTag && (
          <span
            style={{
              fontSize: 'var(--type-caption-size)',
              fontWeight: 500,
              color: 'var(--hue-primary)',
            }}
          >
            Met
          </span>
        )}
      </div>
    </div>
  );
}

function CategoryIconTile({ category }: { readonly category: HabitCategory }) {
  const meta = habitCategoryMeta(category);
  const Icon = meta.Icon;
  return (
    <span
      aria-hidden="true"
      style={{
        width: '24px',
        height: '24px',
        borderRadius: 'var(--radius-sm)',
        background: `color-mix(in srgb, var(--accent-${meta.tokenSuffix}-vivid) 16%, var(--surface-raised))`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 'none',
      }}
    >
      <Icon size={14} color={`var(--accent-${meta.tokenSuffix})`} aria-hidden="true" />
    </span>
  );
}
